/**
 * Where "Download the app" sends someone.
 *
 * Today both buttons (and the /app redirect) point at an AppsFlyer OneLink,
 * which does the store routing itself — that is the interim arrangement
 * agreed with Rahul while we build our own attribution. The point of this
 * module is that switching off OneLink later is a config change, not a code
 * change: unset NEXT_PUBLIC_APP_SMART_LINK and every caller falls through to
 * the real store URLs below, resolved per device.
 *
 * No 'use client' — these are pure functions of their arguments. `/app`
 * resolves its redirect target client-side (`AppStoreAutoRedirect`, so the
 * page paints before a phone is sent on to its store); the badge links are
 * resolved server-side in `StoreBadges`. Both call into this module.
 */

/** App Store listing for the iOS app (numeric id 6773255873). */
export const APP_STORE_URL =
  process.env.NEXT_PUBLIC_APP_STORE_URL || 'https://apps.apple.com/app/id6773255873';

/** Play Store listing for the Android app. */
export const PLAY_STORE_URL =
  process.env.NEXT_PUBLIC_PLAY_STORE_URL ||
  'https://play.google.com/store/apps/details?id=in.docupro.app';

/**
 * Optional single link that supersedes both store URLs. Set to the AppsFlyer
 * OneLink for now; clearing it is how we "get off OneLink" once our own
 * attribution is live.
 */
export const SMART_LINK = process.env.NEXT_PUBLIC_APP_SMART_LINK || '';

export type DevicePlatform = 'ios' | 'android' | 'other';

/**
 * Crawlers must never be bounced to a store: Googlebot's smartphone agent
 * contains "Android", so without this check the mobile redirect below would
 * serve Google something different from what a human sees — which is exactly
 * what cloaking penalties are for.
 */
const BOT_PATTERN =
  /bot|crawl|spider|slurp|facebookexternalhit|embedly|preview|whatsapp|telegram|linkedinbot|quora link|pinterest|vkshare|w3c_validator|lighthouse/i;

export function isBotUserAgent(userAgent: string | null | undefined): boolean {
  return BOT_PATTERN.test(userAgent || '');
}

/**
 * Classifies a User-Agent into the store it should be sent to.
 *
 * "Android" is checked after iOS because some in-app browsers list both
 * tokens. This does NOT catch iPadOS 13+: those report as plain
 * "Macintosh", indistinguishable from a real Mac by UA string alone — that
 * needs `navigator.maxTouchPoints`, which only exists in the browser. See
 * `resolveStoreRedirect`'s `platformOverride` param for the client-side fix.
 */
export function detectPlatform(userAgent: string | null | undefined): DevicePlatform {
  const ua = userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/** The store URL for a platform, honouring SMART_LINK when it is configured. */
export function storeUrlFor(platform: DevicePlatform): string {
  if (SMART_LINK) return SMART_LINK;
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return '';
}

/**
 * The URL a bare visit to /app should be redirected to, or null when the
 * visitor should be shown the landing page instead (desktop, unknown devices,
 * and every crawler).
 *
 * @param platformOverride Skips UA-based detection in favour of a platform
 *   the caller already knows — specifically for iPadOS 13+, which the UA
 *   string alone can't distinguish from a Mac (see `detectPlatform`).
 */
export function resolveStoreRedirect(
  userAgent: string | null | undefined,
  platformOverride?: DevicePlatform
): string | null {
  if (isBotUserAgent(userAgent)) return null;
  const platform = platformOverride ?? detectPlatform(userAgent);
  if (platform === 'other') return null;
  return storeUrlFor(platform) || null;
}

/**
 * Marketing tags we carry over from /app to the store link, so a campaign
 * that tags its ad (`/app?utm_source=facebook`) still shows up as that
 * campaign in reporting instead of arriving anonymous.
 *
 * An allowlist, deliberately, rather than forwarding the whole query string.
 * The redirect target is a OneLink, and AppsFlyer reads some of its own
 * parameters out of the URL it is given — `af_r` among them, which sets a
 * custom redirect destination. Passing arbitrary caller-supplied parameters
 * straight through would therefore hand anyone a way to turn a docupro.in
 * link into a redirect to a site of their choosing. Only inert labels travel.
 */
const FORWARDED_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
  'gclid',
  'ttclid',
  'msclkid',
  'pid',
  'c',
  'ref',
]);

/**
 * Copies the allowlisted marketing tags from an incoming /app request onto
 * the store URL. Parameters the configured target already carries win — that
 * URL was set deliberately and a visitor may not override it.
 */
export function withCampaignParams(
  targetUrl: string,
  incoming: URLSearchParams | undefined
): string {
  if (!incoming) return targetUrl;
  let url: URL;
  try {
    url = new URL(targetUrl);
  } catch {
    // Misconfigured env value; hand it back untouched rather than throwing
    // inside middleware and taking the route down with it.
    return targetUrl;
  }
  for (const [key, value] of incoming) {
    if (!FORWARDED_PARAMS.has(key)) continue;
    if (!value) continue;
    if (url.searchParams.has(key)) continue;
    url.searchParams.set(key, value);
  }
  return url.toString();
}

/**
 * Normalises a Next.js page's `searchParams` into a URLSearchParams, keeping
 * the last value when a key was repeated.
 */
export function toSearchParams(
  input: Record<string, string | string[] | undefined>
): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === 'string') out.set(key, value);
    else if (Array.isArray(value) && value.length > 0) out.set(key, value[value.length - 1]);
  }
  return out;
}

/** Link targets for the two badges on the landing page. */
export const appStoreLink = () => storeUrlFor('ios') || APP_STORE_URL;
export const playStoreLink = () => storeUrlFor('android') || PLAY_STORE_URL;
